import { useMemo, useEffect, useRef, ReactElement, useCallback } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { RowClickedEvent } from '@ag-grid-community/core';
import LoadingOverlayMessage from 'features/queue/grid/loadingOverlayMessage';
import { useFeatureFlagQuery } from 'remote-state/featureFlagsHooks';
import { FEATURE_FLAGS_KEYS } from 'constants/featureFlags';
import { selectDirection } from 'store/globalSlice';
import {
  selectSelectedRowIndex,
  setSelectedRowIndex,
  setPrimarySrId,
} from 'features/queue/grid/actionBar/actions/merge/slice';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import { StyledTable } from './style';
import useMergeTableColumns from './hooks/useMergeTableColumns';
import useMergeTableData from './hooks/useMergeTableData';

const MergeTicketsTable = (): ReactElement => {
  const gridRef = useRef<AgGridReactProps>(null);
  const modules = useMemo(() => [ClientSideRowModelModule], []);
  const columns = useMergeTableColumns();
  const { data, isFetched } = useMergeTableData();
  const dispatch = useDispatch();
  const selectedRowIndex = useSelector(selectSelectedRowIndex);

  const { data: isRTLEnabled } = useFeatureFlagQuery({
    flagKey: FEATURE_FLAGS_KEYS.RTL_ENABLED,
    defaultValue: false,
  });

  const direction = useSelector(selectDirection);

  const isRTL = useMemo(() => isRTLEnabled && direction === 'rtl', [isRTLEnabled, direction]);

  useEffect(() => {
    if (!isFetched) {
      gridRef.current?.api?.showLoadingOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, [isFetched, gridRef]);

  const handleRowClick = useCallback(
    (event: RowClickedEvent) => {
      const target = event.event?.target as HTMLElement;
      const checkboxElement = target?.closest('[data-testid^="merge-ticket-checkbox-"]') as HTMLInputElement;

      // If the checkbox is being unchecked, clear the ag grid row selection
      if (checkboxElement) {
        if (!checkboxElement.checked) {
          gridRef.current?.api?.deselectAll();
        }
        return;
      }

      const rowIndex = event.rowIndex;
      const srId = event.data?.[0]?.value;

      const isSelected = rowIndex === selectedRowIndex;

      // If row is being deselected, clear the checkbox selection
      if (isSelected) {
        gridRef.current?.api?.deselectAll();
      }

      batch(() => {
        dispatch(setSelectedRowIndex(isSelected ? null : rowIndex));
        dispatch(setPrimarySrId(isSelected ? null : srId));
      });
    },
    [dispatch, selectedRowIndex],
  );

  return (
    <StyledTable className="ag-theme-alpine" data-testid="merge-sr-grid">
      <AgGridReact
        //@ts-ignore
        ref={gridRef}
        enableRtl={isRTL}
        modules={modules}
        columnDefs={columns}
        onRowClicked={handleRowClick}
        loadingOverlayComponent={LoadingOverlayMessage}
        rowData={data?.serviceRequests}
        rowSelection="single"
        domLayout="normal"
        suppressCellFocus
      />
    </StyledTable>
  );
};

export default MergeTicketsTable;
